import { getAxiosInstance } from '../../react/utils/networkUtil';
import { PaginationQuery, PaginationResult } from '../models/AppPagination';
import { Syncable } from '../models/Syncable';
import { CollectItemFile_Base } from '../models/CollectItemFile';
import { User_Base } from '../models/User';

// get syncable page
export type CollectItemFile_GetSyncablePage_RequestQuery = PaginationQuery & {
  collect_form_uuid?: string;
  collect_item_uuid?: string;
  user_id?: number;
};
export type CollectItemFile_GetSyncablePage_Response = PaginationResult<Syncable>;
export const remote_getPageSyncable = async (
  request: CollectItemFile_GetSyncablePage_RequestQuery,
): Promise<CollectItemFile_GetSyncablePage_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-item-files-ext/get-page-syncable`, {
    params: request,
  });
  return response.data;
};

// get list
export type CollectItemFile_GetListWithUUIDs_RequestBody = {
  collect_form_uuid: string;
  uuids: string[];
}
export type CollectItemFile_GetListWithUUIDs_ResponseItem = CollectItemFile_Base & {
  user: User_Base;
};
export type CollectItemFile_GetListWithUUIDs_Response = Array<CollectItemFile_GetListWithUUIDs_ResponseItem>;
export const remote_getListWithUUIDs = async (
  request: CollectItemFile_GetListWithUUIDs_RequestBody,
): Promise<CollectItemFile_GetListWithUUIDs_Response> => {
  const response = await getAxiosInstance().post(`/api/collect-item-files-ext/get-list-with-uuids`, request);
  return response.data;
};

// ── Download URL helpers ─────────────────────────────────────────────────────

export type CollectItemFileDownloadVariant = 'original' | 'thumbnail' | 'hd' | 'stamp';

/**
 * Build the download URL for a CollectItemFile with optional processing.
 * The URL points to the authenticated backend endpoint:
 *   GET /api/geocollect/restore/download-file/:uuid
 *
 * Variants:
 *  - 'original'   → no params, auto-rotate EXIF only
 *  - 'thumbnail'  → ?x-oss-process=image/resize,w_300 (cached as __thumbnail)
 *  - 'hd'         → ?x-oss-process=image/resize,w_hd  (cached as __hd)
 *  - 'stamp'      → ?stamp=true (HD + metadata overlay, cached as __stamp)
 */
export const getCollectItemFileDownloadUrl = (
  uuid: string,
  variant: CollectItemFileDownloadVariant = 'original',
  refresh = false,
): string => {
  const base = `/api/geocollect/restore/download-file/${uuid}`;
  const params = new URLSearchParams();

  if (variant === 'thumbnail') params.set('x-oss-process', 'image/resize,w_300');
  else if (variant === 'hd')   params.set('x-oss-process', 'image/resize,w_hd');
  else if (variant === 'stamp') params.set('stamp', 'true');

  if (refresh) params.set('refresh', 'true');

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/**
 * Fetch a CollectItemFile via authenticated axios and return a blob URL.
 * Mirrors useAuthImageUrl but works for CollectItemFile (not FreePhoto).
 */
export const fetchCollectItemFileAsBlob = async (
  uuid: string,
  variant: CollectItemFileDownloadVariant = 'thumbnail',
): Promise<string | null> => {
  try {
    const params: Record<string, string> = {};
    if (variant === 'thumbnail') params['x-oss-process'] = 'image/resize,w_300';
    else if (variant === 'hd')   params['x-oss-process'] = 'image/resize,w_hd';
    else if (variant === 'stamp') params['stamp'] = 'true';

    const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';

    const response = await getAxiosInstance().get(
      `/api/geocollect/restore/download-file/${uuid}${qs}`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data);
  } catch {
    return null;
  }
};

export const CollectItemFileAPIs = {
  remote_getListWithUUIDs,
  remote_getPageSyncable,
  getCollectItemFileDownloadUrl,
  fetchCollectItemFileAsBlob,
};
